<script setup lang="ts">
/**
 * Dev-only 주소 비교 결과 모달 (그룹별).
 *   props.addressGroups = { Residential: [...], Service: [...], Business: [...], BO: [...] }
 *   각 그룹 내부에서만 비교 — 그룹 간은 비교하지 않는다.
 *   각 그룹마다 backend POST /api/dev/address-compare 를 별도로 호출 후 탭으로 결과 표시.
 */
import { ref, computed, watch } from 'vue';
import { apiClient } from '@/shared/api/client';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useDialog } from '@/shared/composables/useDialog';
import { isProduction } from '@/shared/config/env';

const props = defineProps<{
  modelValue: boolean;
  addressGroups: Record<string, Array<Record<string, any>>>;
  title?: string;
  spcCompactUi?: boolean;
  dialogMode?: 'compare' | 'resync';
  /** When set and present in addressGroups, this tab is selected on open; otherwise first group. */
  initialGroupTab?: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'applied'): void;
}>();
const { alert, confirm } = useDialog();

const dialog = ref(props.modelValue);
watch(() => props.modelValue, (v) => { dialog.value = v; });
watch(dialog, (v) => emit('update:modelValue', v));

interface CompareScoreItem {
  index: number;
  address: Record<string, any>;
  scoreVsRecommended: number;
  scoreVsRecommendedPct: string;
  verdict: string;
  pass: boolean;
  avgScore: number;
  avgScorePct: string;
  completeness: number;
  isRecommended: boolean;
  detail?: {
    postcodeMatch?: boolean;
    stateMatch?: boolean;
    countryMatch?: boolean;
    streetScore?: number;
    scoreNormal?: number;
    scoreSwapped?: number;
    subsetScore?: number;
    normalizedA?: Record<string, any>;
    normalizedB?: Record<string, any>;
  };
}

interface CompareGroupItem {
  canonicalKey: string;
  members: Array<Record<string, any>>;
  count: number;
}

interface CompareResult {
  total: number;
  recommended: Record<string, any> | null;
  recommendedIndex: number;
  avgScore: number;
  avgScorePct: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  scores: CompareScoreItem[];
  groups: CompareGroupItem[];
  summary: string;
}

interface GroupState {
  loading: boolean;
  error: string | null;
  result: CompareResult | null;
  expandedRow: number | null;
}

const groupNames = computed(() => Object.keys(props.addressGroups || {}));
const groupStates = ref<Record<string, GroupState>>({});
const activeTab = ref<string>('');
const selectedScoreIndex = ref<Record<string, number | null>>({});
const isApplying = ref(false);
const isReSyncMode = computed(() => props.dialogMode === 'resync');

function makeInitialState(): GroupState {
  return { loading: false, error: null, result: null, expandedRow: null };
}

async function runOne(name: string) {
  const state = groupStates.value[name];
  if (!state) return;
  const items = props.addressGroups[name] || [];
  state.loading = true;
  state.error = null;
  state.result = null;
  state.expandedRow = null;
  if (items.length === 0) {
    state.error = '비교할 주소가 없습니다.';
    state.loading = false;
    return;
  }
  try {
    const res = await apiClient.post('/dev/address-compare', { addresses: items });
    const data = (res as any)?.data || res;
    if (data?.success === false) {
      state.error = data?.error || 'Compare failed.';
    } else {
      state.result = (data?.result || data?.data?.result) as CompareResult;
    }
  } catch (e: any) {
    state.error = e?.message || 'Network error.';
  } finally {
    state.loading = false;
  }
}

async function runAll() {
  // 모든 그룹 초기화 + 병렬 호출.
  const names = groupNames.value;
  groupStates.value = {};
  selectedScoreIndex.value = {};
  for (const n of names) groupStates.value[n] = makeInitialState();
  if (names.length > 0) {
    const preferred =
      props.initialGroupTab && names.includes(props.initialGroupTab) ? props.initialGroupTab : names[0];
    activeTab.value = preferred;
  } else {
    activeTab.value = '';
  }
  await Promise.all(names.map(runOne));
}

watch(dialog, (open) => {
  if (open) {
    void loadCountryBasecodeMap();
    void runAll();
  } else {
    activeTab.value = '';
  }
});

// country baseCode (B15xxx) → 사람 이름 매핑.
//   모달 첫 오픈 시 baseCodeApi.getCodesByMainCodeOnly('B15') 한 번 호출해 캐시.
//   캐시되어 있지 않으면 raw 값 그대로 표시 (network 실패 fallback).
const countryBasecodeMap = ref<Record<string, string>>({});
const baseCodeLoaded = ref(false);

async function loadCountryBasecodeMap(): Promise<void> {
  if (baseCodeLoaded.value) return;
  try {
    const res: any = await baseCodeApi.getCodesByMainCodeOnly('B15');
    const codes: any[] = res?.data?.codes || res?.codes || res?.data || [];
    const map: Record<string, string> = {};
    for (const c of codes) {
      const k = String(c?.totalCode || c?.TOTAL_CODE || '').trim().toUpperCase();
      const v = String(c?.codeName || c?.CODE_NAME || '').trim();
      if (k && v) map[k] = v;
    }
    countryBasecodeMap.value = map;
  } catch (e) {
    console.warn('[AddressCompareDevDialog] B15 basecode load failed (fallback to raw)', e);
  } finally {
    baseCodeLoaded.value = true;
  }
}

function _resolveCountry(v: any): string {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const upper = s.toUpperCase();
  // basecode 형태 (B15xxx) — 캐시 lookup 후 fallback 으로 raw 값.
  if (/^B\d{4,}$/.test(upper)) {
    return countryBasecodeMap.value[upper] || s;
  }
  return s;
}

/** 트레일링/leading 콤마·공백을 제거해 ", , " 같은 빈 콤마 패턴을 방지. */
function _cleanField(v: any): string {
  if (v == null) return '';
  return String(v)
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();
}

function fmtAddrLine(a: Record<string, any>): string {
  const parts = [
    _cleanField(a.street1 || a.street01 || a.address1 || a.addressLine1),
    _cleanField(a.street2 || a.street02 || a.address2 || a.addressLine2),
    _cleanField(a.street3 || a.street03 || a.address3 || a.addressLine3),
    _cleanField(a.city),
    _cleanField(a.postcode || a.post_code || a.zip || a.postalCode),
    _cleanField(a.state),
    _resolveCountry(a.country),
  ].filter((v: string) => v !== '');
  return parts.length ? parts.join(', ') : '(empty)';
}

function isCountryOnlyAddress(a: Record<string, any> | null | undefined): boolean {
  if (!a || typeof a !== 'object') return false;
  const hasStreet = [
    a.street1, a.street01, a.address1, a.addressLine1,
    a.street2, a.street02, a.address2, a.addressLine2,
    a.street3, a.street03, a.address3, a.addressLine3,
  ].some((v: any) => String(v ?? '').trim() !== '');
  const hasPostCode = String(a.postcode ?? a.post_code ?? a.zip ?? a.postalCode ?? '').trim() !== '';
  const hasCity = String(a.city ?? '').trim() !== '';
  const hasState = String(a.state ?? '').trim() !== '';
  const hasCountry = String(a.country ?? '').trim() !== '';
  return hasCountry && !hasStreet && !hasPostCode && !hasCity && !hasState;
}

function getDisplayScores(name: string): CompareScoreItem[] {
  const scores = groupStates.value[name]?.result?.scores || [];
  if (!props.spcCompactUi) return scores;
  const nonCountryOnly = scores.filter((s) => !isCountryOnlyAddress(s.address));
  const mismatches = nonCountryOnly.filter((s) => !s.pass);
  const base = mismatches.length > 0
    ? mismatches
    : (nonCountryOnly.length > 0
      ? [[...nonCountryOnly].sort((a, b) => a.index - b.index)[0]].filter(Boolean) as CompareScoreItem[]
      : []);

  // Recommended 행은 Scores에서 항상 확인 가능하도록 포함.
  const recommendedIndex = groupStates.value[name]?.result?.recommendedIndex;
  if (typeof recommendedIndex === 'number') {
    const recommendedRow = nonCountryOnly.find((s) => s.index === recommendedIndex);
    if (recommendedRow) {
      base.push(recommendedRow);
    }
  }

  // DEV-SPC compact: 동일 주소 문자열은 최소 인덱스 1건만 노출.
  const dedupedByAddress = new Map<string, CompareScoreItem>();
  for (const score of base) {
    const key = fmtAddrLine(score.address).toUpperCase();
    const existing = dedupedByAddress.get(key);
    const existingIsRecommended = !!existing?.isRecommended;
    const incomingIsRecommended = !!score?.isRecommended;
    if (
      !existing ||
      (incomingIsRecommended && !existingIsRecommended) ||
      (!existingIsRecommended && !incomingIsRecommended && score.index < existing.index)
    ) {
      dedupedByAddress.set(key, score);
    }
  }
  return Array.from(dedupedByAddress.values()).sort((a, b) => a.index - b.index);
}

function getDisplayInputItems(name: string): Array<{ item: Record<string, any>; index: number }> {
  const all = props.addressGroups[name] || [];
  if (!props.spcCompactUi) return all.map((item, index) => ({ item, index }));

  const recommendedIndex = groupStates.value[name]?.result?.recommendedIndex;
  const failedIndexes = new Set(getDisplayScores(name).map((s) => s.index));
  if (typeof recommendedIndex === 'number') {
    failedIndexes.add(recommendedIndex);
  }
  const filtered = all
    .map((item, index) => ({ item, index }))
    .filter(({ item, index }) => failedIndexes.has(index) && !isCountryOnlyAddress(item));

  // DEV-SPC compact: 동일 주소 문자열은 최소 인덱스 1건만 노출.
  const dedupedByAddress = new Map<string, { item: Record<string, any>; index: number }>();
  for (const entry of filtered) {
    const key = fmtAddrLine(entry.item).toUpperCase();
    const existing = dedupedByAddress.get(key);
    if (!existing || entry.index < existing.index) {
      dedupedByAddress.set(key, entry);
    }
  }
  return Array.from(dedupedByAddress.values()).sort((a, b) => a.index - b.index);
}

function confidenceColor(c: string | undefined): string {
  if (c === 'HIGH') return 'success';
  if (c === 'MEDIUM') return 'warning';
  return 'error';
}

function verdictColor(v: string | undefined): string {
  if (v === 'SAME') return 'success';
  if (v === 'LIKELY_SAME') return 'success';
  if (v === 'REVIEW') return 'warning';
  return 'error';
}

function toggleExpand(group: string, idx: number) {
  const s = groupStates.value[group];
  if (!s) return;
  s.expandedRow = s.expandedRow === idx ? null : idx;
}

function isScoreSelected(group: string, idx: number): boolean {
  return selectedScoreIndex.value[group] === idx;
}

function toggleScoreSelection(group: string, idx: number) {
  const current = selectedScoreIndex.value[group] ?? null;
  selectedScoreIndex.value[group] = current === idx ? null : idx;
}

function resolveAddrTypeByGroupName(groupName: string): string | null {
  if (groupName === 'Residential') return 'M04001';
  if (groupName === 'Service') return 'M04002';
  if (groupName === 'Business / BO') return 'M04003';
  return null;
}

function hasAnyStreetLine(addr: Record<string, any> | null | undefined): boolean {
  if (!addr || typeof addr !== 'object') return false;
  return ['street01', 'street02', 'street03'].some((k) => String(addr?.[k] || '').trim() !== '');
}

function resolveScoreMatchRate(score: CompareScoreItem | null | undefined): number {
  if (!score) return 0;
  const fromPct = Number.parseFloat(String(score.scoreVsRecommendedPct || '').replace('%', '').trim());
  if (Number.isFinite(fromPct)) return fromPct;
  const fromRaw = Number(score.scoreVsRecommended);
  return Number.isFinite(fromRaw) ? Math.max(0, Math.min(100, fromRaw * 100)) : 0;
}

async function applySelectedScores() {
  try {
    if (!props.spcCompactUi) {
      await alert({ title: 'Info', message: 'Apply is available only for DEV-SPC mode.', type: 'info' });
      return;
    }

    const items: Array<{ contactCd: string; addrType: string; addrJson: Record<string, any>; matchRate: number }> = [];
    for (const name of groupNames.value) {
      const idx = selectedScoreIndex.value[name];
      if (typeof idx !== 'number') continue;
      const score = getDisplayScores(name).find((s) => s.index === idx);
      if (!score) continue;
      const addrType = resolveAddrTypeByGroupName(name);
      const contactCd = String(score.address?._contactCd || '').trim();
      if (!addrType || !contactCd) continue;
      if (!hasAnyStreetLine(score.address)) continue;
      items.push({
        contactCd,
        addrType,
        addrJson: score.address,
        matchRate: resolveScoreMatchRate(score),
      });
    }

    if (!items.length) {
      await alert({ title: 'Info', message: 'No valid checked rows to apply.', type: 'info' });
      return;
    }

    const userConfirmed = await confirm({
      title: 'Confirm',
      message:
        'Apply the selected address consolidation to the contact profile? You can review the result after the page refreshes.',
      type: 'question',
    });
    if (!userConfirmed) return;

    isApplying.value = true;
    const res: any = await apiClient.post('/dev/spc-contact-addresses/apply-profile-match-bulk', { items });
    const data = (res as any)?.data || res;
    const failed = Array.isArray(data?.failed) ? data.failed.length : 0;
    await alert({
      title: 'Success',
      message: 'Contact updated successfully.',
      type: failed > 0 ? 'warning' : 'success',
    });
    dialog.value = false;
    emit('applied');
  } catch (e: any) {
    await alert({
      title: 'Error',
      message: e?.message || 'Apply failed.',
      type: 'error',
    });
  } finally {
    isApplying.value = false;
  }
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="1200" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-compare-horizontal</v-icon>
        <span>{{ title || 'Address Compare (DEV)' }}</span>
        <v-chip v-if="!isReSyncMode" class="ml-2" size="x-small" color="warning" variant="tonal">DEV</v-chip>
        <v-spacer />
        <v-btn icon variant="text" size="small" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <!-- 그룹 없음 -->
      <v-card-text v-if="groupNames.length === 0" class="pa-6 text-center">
        <v-icon size="48" color="grey-lighten-1">mdi-map-marker-off-outline</v-icon>
        <p class="text-grey mt-3 mb-0">비교할 주소가 없습니다 — 모든 그룹이 비어있습니다.</p>
      </v-card-text>

      <!-- 그룹별 탭 -->
      <template v-else>
        <v-tabs v-model="activeTab" color="primary" align-tabs="start" density="compact">
          <v-tab
            v-for="name in groupNames"
            :key="name"
            :value="name"
          >
            {{ name }}
            <v-chip class="ml-2" size="x-small" variant="tonal">
              {{ getDisplayInputItems(name).length }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <v-divider />

        <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
          <v-window v-model="activeTab">
            <v-window-item
              v-for="name in groupNames"
              :key="name"
              :value="name"
            >
              <!-- 그룹 input preview -->
              <div v-if="!isReSyncMode" class="mb-3">
                <div class="text-subtitle-2 mb-1">
                  Input — {{ name }} ({{ getDisplayInputItems(name).length }})
                </div>
                <v-list density="compact" lines="two">
                  <v-list-item
                    v-for="entry in getDisplayInputItems(name)"
                    :key="entry.index"
                    :title="props.spcCompactUi ? fmtAddrLine(entry.item) : `#${entry.index} ${entry.item._label || entry.item.label || entry.item.id || ''}`"
                    :subtitle="props.spcCompactUi ? '' : fmtAddrLine(entry.item)"
                    class="px-0"
                  />
                </v-list>
              </div>

              <v-divider class="my-3" />

              <div v-if="!groupStates[name]" class="text-grey">로딩 대기...</div>

              <template v-else>
                <div v-if="groupStates[name].loading" class="text-center py-6">
                  <v-progress-circular indeterminate color="primary" size="32" />
                  <div class="text-caption mt-2">Comparing {{ name }}...</div>
                </div>

                <div v-else-if="groupStates[name].error" class="text-error">
                  <v-icon size="20">mdi-alert</v-icon>
                  {{ groupStates[name].error }}
                </div>

                <template v-else-if="groupStates[name].result">
                  <!-- summary: local/dev only (운영에서는 내부 진단 문구 숨김) -->
                  <div v-if="!isProduction()" class="d-flex align-center mb-3">
                    <v-chip
                      :color="confidenceColor(groupStates[name].result!.confidence)"
                      variant="flat"
                      size="small"
                      class="mr-2"
                    >
                      {{ groupStates[name].result!.confidence }} · {{ groupStates[name].result!.avgScorePct }}
                    </v-chip>
                    <span class="text-body-2">{{ groupStates[name].result!.summary }}</span>
                  </div>

                  <!-- recommended -->
                  <v-card variant="tonal" color="primary" class="mb-3">
                    <v-card-text class="py-2">
                      <div class="text-caption text-grey">Recommended (idx {{ groupStates[name].result!.recommendedIndex }})</div>
                      <div class="text-body-1 font-weight-medium">
                        {{ groupStates[name].result!.recommended ? fmtAddrLine(groupStates[name].result!.recommended!) : '—' }}
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- scores table -->
                  <div class="text-subtitle-2 mb-1">
                    Scores ({{ getDisplayScores(name).length }})<span v-if="!isReSyncMode"> — 행 클릭 = 진단 펼침</span>
                  </div>
                  <v-table density="compact" class="mb-3">
                    <thead>
                      <tr>
                        <th>sel</th>
                        <th>address</th>
                        <th v-if="!isReSyncMode"></th>
                        <th v-if="!isReSyncMode">idx</th>
                        <th v-if="!isReSyncMode" class="text-right">vs rec</th>
                        <th v-if="!isReSyncMode">verdict</th>
                        <th v-if="!isReSyncMode">pass</th>
                        <th v-if="!isReSyncMode" class="text-right">avg</th>
                        <th v-if="!isReSyncMode" class="text-right">complete</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="s in getDisplayScores(name)" :key="s.index">
                        <tr
                          :class="[
                            s.isRecommended ? 'bg-primary-lighten-5' : '',
                            !isReSyncMode ? 'cursor-pointer' : '',
                          ]"
                          @click="!isReSyncMode && toggleExpand(name, s.index)"
                        >
                          <td>
                            <v-checkbox-btn
                              :model-value="isScoreSelected(name, s.index)"
                              density="compact"
                              @click.stop
                              @update:model-value="toggleScoreSelection(name, s.index)"
                            />
                          </td>
                          <td v-if="!isReSyncMode">
                            <v-icon size="16">
                              {{ groupStates[name].expandedRow === s.index ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                            </v-icon>
                          </td>
                          <td v-if="!isReSyncMode">
                            {{ s.index }}
                            <v-icon v-if="s.isRecommended" size="14" color="primary">mdi-star</v-icon>
                          </td>
                          <td class="text-caption">
                            {{ fmtAddrLine(s.address) }}
                            <v-chip
                              v-if="isReSyncMode && s.isRecommended"
                              color="primary"
                              size="x-small"
                              variant="flat"
                              class="ml-2"
                            >
                              Recommended
                            </v-chip>
                          </td>
                          <td v-if="!isReSyncMode" class="text-right">{{ s.scoreVsRecommendedPct }}</td>
                          <td v-if="!isReSyncMode">
                            <v-chip :color="verdictColor(s.verdict)" size="x-small" variant="tonal">
                              {{ s.verdict }}
                            </v-chip>
                          </td>
                          <td v-if="!isReSyncMode">
                            <v-icon size="16" :color="s.pass ? 'success' : 'error'">
                              {{ s.pass ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                          </td>
                          <td v-if="!isReSyncMode" class="text-right">{{ s.avgScorePct }}</td>
                          <td v-if="!isReSyncMode" class="text-right">{{ s.completeness }}/7</td>
                        </tr>
                        <tr
                          v-if="!isReSyncMode && groupStates[name].expandedRow === s.index && s.detail"
                          class="bg-grey-lighten-5"
                        >
                          <td colspan="9" class="pa-3">
                            <div class="d-flex flex-wrap ga-3" style="font-size: 12px;">
                              <div>
                                <div class="font-weight-bold mb-1">Field match</div>
                                <div>postcode: <v-icon size="14" :color="s.detail.postcodeMatch ? 'success' : 'error'">{{ s.detail.postcodeMatch ? 'mdi-check' : 'mdi-close' }}</v-icon></div>
                                <div>state:    <v-icon size="14" :color="s.detail.stateMatch    ? 'success' : 'error'">{{ s.detail.stateMatch    ? 'mdi-check' : 'mdi-close' }}</v-icon></div>
                                <div>country:  <v-icon size="14" :color="s.detail.countryMatch  ? 'success' : 'error'">{{ s.detail.countryMatch  ? 'mdi-check' : 'mdi-close' }}</v-icon></div>
                              </div>
                              <div>
                                <div class="font-weight-bold mb-1">Street scores</div>
                                <div>streetScore:   {{ ((s.detail.streetScore ?? 0) * 100).toFixed(1) }}%</div>
                                <div>scoreNormal:   {{ ((s.detail.scoreNormal ?? 0) * 100).toFixed(1) }}%</div>
                                <div>scoreSwapped:  {{ ((s.detail.scoreSwapped ?? 0) * 100).toFixed(1) }}%</div>
                                <div>subsetScore:   {{ ((s.detail.subsetScore ?? 0) * 100).toFixed(1) }}%</div>
                              </div>
                              <div style="min-width: 200px;">
                                <div class="font-weight-bold mb-1">Normalized A (recommended)</div>
                                <div v-for="(v, k) in s.detail.normalizedA" :key="`a-${k}`">
                                  <strong>{{ k }}:</strong> {{ v || '(empty)' }}
                                </div>
                              </div>
                              <div style="min-width: 200px;">
                                <div class="font-weight-bold mb-1">Normalized B (this row)</div>
                                <div v-for="(v, k) in s.detail.normalizedB" :key="`b-${k}`">
                                  <strong>{{ k }}:</strong> {{ v || '(empty)' }}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </v-table>

                  <!-- groups -->
                  <template v-if="!isReSyncMode">
                    <div class="text-subtitle-2 mb-1">Groups ({{ groupStates[name].result!.groups.length }})</div>
                    <v-list density="compact" lines="two">
                      <v-list-item
                        v-for="(g, i) in groupStates[name].result!.groups"
                        :key="i"
                        :title="`Group ${i + 1} — ${g.count} item(s)`"
                        :subtitle="g.canonicalKey"
                        class="px-0"
                      >
                        <template #append>
                          <v-chip size="x-small" variant="outlined">{{ g.count }}</v-chip>
                        </template>
                      </v-list-item>
                    </v-list>
                  </template>
                </template>
              </template>
            </v-window-item>
          </v-window>
        </v-card-text>
      </template>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn v-if="!isReSyncMode" variant="tonal" :disabled="groupNames.length === 0" @click="runAll">
          <v-icon start size="16">mdi-refresh</v-icon>
          Re-run all
        </v-btn>
        <v-btn
          v-if="props.spcCompactUi"
          color="primary"
          variant="outlined"
          :loading="isApplying"
          @click="applySelectedScores"
        >
          <v-icon start size="16">mdi-check-decagram-outline</v-icon>
          Select Address Consolidation
        </v-btn>
        <v-btn color="primary" variant="flat" @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
